import * as React from 'react';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import SnackbarContent from '@mui/material/SnackbarContent';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { withTranslation } from 'react-i18next';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';

import { MANTY_BLACK, MANTY_ORANGE } from 'styles/theme';
import './_snackbarNotification.scss';

import { green, red } from '@mui/material/colors';

const variantIcon = {
    success: <CheckCircleIcon />,
    warning: <WarningIcon />,
    error: <ErrorIcon />,
    info: <InfoIcon />,
};

const variantColor = {
    success: green[600],
    error: red[600],
    info: MANTY_BLACK,
    warning: MANTY_ORANGE,
};

export type SnackbarVariant = 'success' | 'error' | 'info' | 'warning';
type Props = {
    open: boolean;
    t: TranslationT;
    close: FlowAnyFunction;
    action:
        | {
              message: string;
              onClick: FlowAnyFunction;
          }
        | null
        | undefined;
    variant: SnackbarVariant | null | undefined;
    message: string | null | undefined;
};

let snackTimeout: NodeJS.Timeout;
const AUTO_HIDE_DURATION = 8000;

class SnackbarNotification extends React.Component<Props> {
    componentDidUpdate(prevProps: Props) {
        // We set a timeout to automatically close the snack after a few seconds.
        // We do that here an not in the reducer because we want to be able to
        // cancel this timeout if the snack is manually closed.
        // If we don't, the timeout closes the new snacks! It has happened in e2e tests.
        if (this.props.open && !prevProps.open) {
            snackTimeout = setTimeout(this.props.close, AUTO_HIDE_DURATION);
        }
    }

    getSnackActions = () => {
        const snackActions = [];
        if (this.props.action != null) {
            const onClick = () => {
                if (this.props.action) this.props.action.onClick();
                this.closeSnackCancelTimeout();
            };
            snackActions.push(
                // @ts-expect-error [TS migration] Was not detected by flow
                <Button
                    key="snackbar-button"
                    data-testid="e2e-snackbar-button"
                    color="inherit"
                    dense
                    onClick={onClick}
                >
                    {this.props.action.message}
                </Button>,
            );
        }
        snackActions.push(
            <Box key="close" sx={{ ml: 0.5, mr: -1.5 }}>
                <IconButton
                    aria-label="Close"
                    color="inherit"
                    data-testid="e2e-snackbar-close"
                    onClick={this.closeSnackCancelTimeout}
                    size="large"
                >
                    <CloseIcon />
                </IconButton>
            </Box>,
        );
        return snackActions;
    };

    getIcon = (iconName?: SnackbarVariant | null): React.ReactElement => {
        if (!iconName) return <></>;

        const icon = variantIcon[iconName];
        return (
            <Box sx={{ mr: 2 }} className="icon">
                {icon}
            </Box>
        );
    };

    closeSnackCancelTimeout = () => {
        clearTimeout(snackTimeout);
        this.props.close();
    };

    render() {
        const backgroundStyle = this.props.variant
            ? {
                  backgroundColor: variantColor[this.props.variant],
              }
            : {};
        const actions = this.getSnackActions();
        const icon = this.getIcon(this.props.variant);
        const message = this.props.t(this.props.message ?? '');

        return (
            <Snackbar
                data-testid="e2e-snackbar"
                open={this.props.open}
                autoHideDuration={AUTO_HIDE_DURATION}
                key={message}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
            >
                <SnackbarContent
                    style={backgroundStyle}
                    message={
                        <div className="rich-snack-message">
                            <div className="icon-and-text-container">
                                {icon}
                                {message}
                            </div>
                            {actions}
                        </div>
                    }
                />
            </Snackbar>
        );
    }
}

const TranslatedSnackbarNotification =
    withTranslation('backend')(SnackbarNotification);
TranslatedSnackbarNotification.displayName = 'SnackbarNotification';
export default TranslatedSnackbarNotification;
